/*
  <reviews-bold> — builds the premium reviews design from REAL Judge.me data.

  Reads the inert <template data-jdgm-source> (the product's judgeme.widget
  metafield HTML), extracts the aggregate, histogram and first-page reviews via
  the DOM API (robust — no string parsing), and renders:
    summary (big score + amber stars + count + write button) · amber histogram ·
    responsive card grid (avatar, name, relative date, verified pill, stars, body, photo).

  Butonul de scris recenzia e linkul real al Judge.me, imbracat prin CSS — vezi
  section-reviews-bold.css. Guarded + defer-loaded. Builds with createElement (no innerHTML injection of review text).
*/
if (!customElements.get('reviews-bold')) {
  customElements.define(
    'reviews-bold',
    class ReviewsBold extends HTMLElement {
      connectedCallback() {
        const tpl = this.querySelector('[data-jdgm-source]');
        this.out = this.querySelector('[data-reviews-bold-out]');
        if (!tpl || !this.out || !('content' in tpl)) return;
        const root = tpl.content;

        const widg = root.querySelector('.jdgm-rev-widg') || root.querySelector('[data-average-rating]');
        const data = {
          heading: this.dataset.heading || 'Recenzii clienți',
          suffix: this.dataset.summarySuffix || 'recenzii',
          avg: widg ? parseFloat(widg.getAttribute('data-average-rating')) || 0 : 0,
          count: widg ? parseInt(widg.getAttribute('data-number-of-reviews'), 10) || 0 : 0,
          hist: {},
          reviews: [],
        };

        root.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((r) => {
          const rating = r.getAttribute('data-rating');
          if (rating) data.hist[rating] = parseFloat(r.getAttribute('data-percentage')) || 0;
        });

        root.querySelectorAll('.jdgm-rev').forEach((rev) => {
          const get = (sel) => rev.querySelector(sel);
          const pic = get('.jdgm-rev__pic-link');
          const picImg = get('.jdgm-rev__pic-img');
          data.reviews.push({
            score: parseFloat((get('.jdgm-rev__rating') || {}).getAttribute?.('data-score')) || 0,
            author: (get('.jdgm-rev__author')?.textContent || '').trim(),
            title: (get('.jdgm-rev__title')?.textContent || '').trim(),
            body: (get('.jdgm-rev__body')?.textContent || '').trim(),
            ts: (get('.jdgm-rev__timestamp')?.getAttribute('data-content') || '').trim(),
            verified: rev.getAttribute('data-verified-buyer') === 'true',
            pic: (pic && pic.getAttribute('href')) || (picImg && (picImg.getAttribute('data-src') || picImg.getAttribute('src'))) || '',
          });
        });

        if (!data.count && !data.reviews.length) return;
        this.render(data);
      }

      el(tag, cls, text) {
        const n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        return n;
      }

      stars(score) {
        const wrap = this.el('span', 'reviews-bold__stars');
        wrap.setAttribute('aria-label', `${score} din 5`);
        wrap.setAttribute('role', 'img');
        for (let i = 1; i <= 5; i++) {
          const s = this.el('span', 'reviews-bold__star', '★');
          if (score >= i) s.classList.add('is-on');
          else if (score >= i - 0.5) s.classList.add('is-half');
          wrap.appendChild(s);
        }
        return wrap;
      }

      relTime(ts) {
        if (!ts) return '';
        const d = new Date(ts.replace(' UTC', 'Z').replace(' ', 'T'));
        if (isNaN(d.getTime())) return '';
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        if (days < 1) return 'astăzi';
        if (days < 7) return days === 1 ? 'acum 1 zi' : `acum ${days} zile`;
        if (days < 30) {
          const w = Math.floor(days / 7);
          return w === 1 ? 'acum 1 săptămână' : `acum ${w} săptămâni`;
        }
        if (days < 365) {
          const m = Math.floor(days / 30);
          return m === 1 ? 'acum 1 lună' : `acum ${m} luni`;
        }
        const y = Math.floor(days / 365);
        return y === 1 ? 'acum 1 an' : `acum ${y} ani`;
      }

      render(d) {
        const frag = document.createDocumentFragment();

        /* ---- Top: summary + histogram ---- */
        const top = this.el('div', 'reviews-bold__top');

        const summary = this.el('div', 'reviews-bold__summary');
        summary.appendChild(this.el('h2', 'reviews-bold__heading', d.heading));
        const scoreRow = this.el('div', 'reviews-bold__scorerow');
        const scoreVal = (Math.round(d.avg * 10) / 10).toFixed(1).replace('.', ',');
        scoreRow.appendChild(this.el('span', 'reviews-bold__score', scoreVal));
        scoreRow.appendChild(this.stars(d.avg));
        summary.appendChild(scoreRow);
        summary.appendChild(this.el('p', 'reviews-bold__count', `pe baza a ${d.count} ${d.suffix}`));
        /* Butonul de „scrie o recenzie" nu mai e al nostru. Unul propriu ar fi trebuit sa
           apese linkul Judge.me printr-un clic sintetic, iar asta nu deschide formularul lor.
           Acum linkul lor real e vizibil si imbracat ca buton in section-reviews-bold.css. */
        top.appendChild(summary);

        const hist = this.el('div', 'reviews-bold__hist');
        for (let r = 5; r >= 1; r--) {
          const pct = d.hist[r] || 0;
          const row = this.el('div', 'reviews-bold__hist-row');
          const lbl = this.el('span', 'reviews-bold__hist-label');
          lbl.appendChild(document.createTextNode(String(r)));
          lbl.appendChild(this.el('span', 'reviews-bold__hist-star', '★'));
          row.appendChild(lbl);
          const track = this.el('span', 'reviews-bold__hist-track');
          const fill = this.el('span', 'reviews-bold__hist-fill');
          fill.style.width = pct + '%';
          track.appendChild(fill);
          row.appendChild(track);
          row.appendChild(this.el('span', 'reviews-bold__hist-pct', pct + '%'));
          hist.appendChild(row);
        }
        top.appendChild(hist);
        frag.appendChild(top);

        /* ---- Card grid ---- */
        if (d.reviews.length) {
          const grid = this.el('div', 'reviews-bold__grid');
          d.reviews.forEach((rv) => {
            const card = this.el('article', 'reviews-bold__card');

            const head = this.el('div', 'reviews-bold__card-head');
            const avatar = this.el('span', 'reviews-bold__avatar', (rv.author || '?').trim().charAt(0).toUpperCase());
            avatar.setAttribute('aria-hidden', 'true');
            head.appendChild(avatar);
            const who = this.el('div', 'reviews-bold__who');
            who.appendChild(this.el('span', 'reviews-bold__name', rv.author || 'Client'));
            if (rv.ts) who.appendChild(this.el('span', 'reviews-bold__date', this.relTime(rv.ts)));
            head.appendChild(who);
            if (rv.verified) {
              const badge = this.el('span', 'reviews-bold__badge');
              badge.appendChild(this.el('span', 'reviews-bold__badge-check', '✓'));
              badge.appendChild(document.createTextNode(' Verificat'));
              head.appendChild(badge);
            }
            card.appendChild(head);

            card.appendChild(this.stars(rv.score));
            if (rv.title) card.appendChild(this.el('p', 'reviews-bold__card-title', rv.title));
            if (rv.body) card.appendChild(this.el('p', 'reviews-bold__card-body', rv.body));
            if (rv.pic) {
              const fig = this.el('div', 'reviews-bold__card-pic');
              const img = document.createElement('img');
              img.src = rv.pic;
              img.loading = 'lazy';
              img.alt = '';
              fig.appendChild(img);
              card.appendChild(fig);
            }
            grid.appendChild(card);
          });
          frag.appendChild(grid);
        }

        this.out.textContent = '';
        this.out.appendChild(frag);
      }
    }
  );
}
